"use client"

import {
    ArrowRight,
    BookOpen,
    Cable,
    Compass,
    GraduationCap,
    ListChecks,
    type LucideIcon,
    Rocket,
    UserCog,
    Users,
} from "lucide-react"

import { BorderBeam } from "@/common/components/ui/border-beam"
import { DotPattern } from "@/common/components/ui/dot-pattern"
import ScrollReveal from "@/common/components/ui/scroll-reveal"
import { Button } from "@/common/components/ui/button"
import { cn } from "@/common/lib/utils"
import { CONNECTORS } from "@/modules/lernpfad/data/connectors"
import type { LernpfadDemoState, Persona } from "@/app/demo/lernpfad/use-lernpfad-demo"

/**
 * The Compass landing page — entry stage of the demo.
 *
 * One story arc down the page: claim → "works with your LMS" wall →
 * how-it-works → persona cards → honest footnote. Every CTA drops into the
 * app stage; the persona cards preselect the matching view so a stakeholder
 * lands directly on their own screen.
 */

const EXTRA_WALL_CHIPS = ["Cornerstone", "Docebo"]

const CAPABILITIES: Array<{ labelKey: string; icon: LucideIcon }> = [
    { labelKey: "cap.catalog", icon: BookOpen },
    { labelKey: "cap.transcript", icon: ListChecks },
    { labelKey: "cap.launch", icon: Rocket },
    { labelKey: "cap.assign", icon: GraduationCap },
]

const PERSONA_CARDS: Array<{ view: Persona; labelKey: string; bodyKey: string; icon: LucideIcon }> = [
    { view: "mitarbeitende", labelKey: "persona.employee", bodyKey: "landing.persona.employee", icon: GraduationCap },
    { view: "fuehrung", labelKey: "persona.manager", bodyKey: "landing.persona.manager", icon: UserCog },
    { view: "hr", labelKey: "persona.hr", bodyKey: "landing.persona.hr", icon: Users },
]

export default function Landing({ state }: { state: LernpfadDemoState }) {
    const { localizer, setStage, setPersona } = state
    const { t } = localizer

    const enter = (view?: Persona) => {
        if (view) setPersona(view)
        setStage("app")
    }

    return (
        <div className="mx-auto w-full max-w-[1100px] px-4 py-10 xl:px-6">
            {/* Hero */}
            <ScrollReveal>
                <section className="relative overflow-hidden rounded-2xl border bg-card p-8 sm:p-12">
                    <DotPattern className="absolute inset-0 opacity-40 [mask-image:radial-gradient(60%_60%_at_50%_35%,black,transparent)]" />
                    <BorderBeam size={220} duration={9} />
                    <div className="relative">
                        <div className="flex items-center gap-2.5">
                            <Compass className="size-8 text-primary" />
                            <span className="text-2xl font-bold tracking-tight">Compass</span>
                        </div>
                        <h1 className="mt-6 max-w-2xl text-4xl font-semibold leading-tight tracking-tight sm:text-5xl">
                            {t("landing.claim")}
                        </h1>
                        <p className="mt-4 max-w-2xl text-lg leading-relaxed text-muted-foreground">
                            {t("landing.sub")}
                        </p>
                        {/* The guided path leads, because most people open this
                            link with nobody presenting it. Free exploration
                            stays one click away for anyone who wants it. */}
                        <div className="mt-8 flex flex-wrap items-center gap-3">
                            <Button size="lg" onClick={state.startTour}>
                                {t("tour.start")}
                                <ArrowRight className="size-4" />
                            </Button>
                            <Button size="lg" variant="outline" onClick={() => enter()}>
                                {t("landing.cta")}
                            </Button>
                        </div>
                    </div>
                </section>
            </ScrollReveal>

            {/* Works-with wall */}
            <ScrollReveal delay={100}>
                <section className="mt-14">
                    <h2 className="text-center text-xl font-semibold">{t("landing.wall.title")}</h2>
                    <p className="mx-auto mt-1.5 max-w-xl text-center text-sm text-muted-foreground">
                        {t("landing.wall.sub")}
                    </p>
                    <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                        {[...CONNECTORS.map((connector) => connector.name), ...EXTRA_WALL_CHIPS].map(
                            (name) => (
                                <span
                                    key={name}
                                    className="rounded-lg border bg-card px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
                                >
                                    {name}
                                </span>
                            ),
                        )}
                    </div>
                    <div className="mt-5 flex flex-wrap items-center justify-center gap-x-5 gap-y-1.5">
                        {CAPABILITIES.map(({ labelKey, icon: Icon }) => (
                            <span
                                key={labelKey}
                                className="inline-flex items-center gap-1.5 text-xs text-muted-foreground"
                            >
                                <Icon className="size-3.5 text-primary" />
                                {t(labelKey as never)}
                            </span>
                        ))}
                    </div>
                </section>
            </ScrollReveal>

            {/* How it works */}
            <ScrollReveal delay={150}>
                <section className="mt-14">
                    <h2 className="text-center text-xl font-semibold">{t("landing.how.title")}</h2>
                    <div className="mt-6 grid gap-4 sm:grid-cols-3">
                        {([1, 2, 3] as const).map((step) => (
                            <div key={step} className="relative lp-card rounded-xl border bg-card p-5">
                                <span className="flex size-8 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground">
                                    {step}
                                </span>
                                <h3 className="mt-3 font-semibold">
                                    {t(`landing.how.${step}.title` as never)}
                                </h3>
                                <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                                    {t(`landing.how.${step}.body` as never)}
                                </p>
                                {step === 1 && (
                                    <Cable className="absolute right-4 top-4 size-4 text-muted-foreground/40" />
                                )}
                            </div>
                        ))}
                    </div>
                </section>
            </ScrollReveal>

            {/* Personas */}
            <ScrollReveal delay={200}>
                <section className="mt-14">
                    <h2 className="text-center text-xl font-semibold">{t("landing.personas.title")}</h2>
                    <div className="mt-6 grid gap-4 sm:grid-cols-3">
                        {PERSONA_CARDS.map(({ view, labelKey, bodyKey, icon: Icon }) => (
                            <button
                                key={view}
                                type="button"
                                onClick={() => enter(view)}
                                className={cn(
                                    "group lp-card rounded-xl border bg-card p-5 text-left transition-all",
                                    "lp-card-hover",
                                )}
                            >
                                <Icon className="size-5 text-primary" />
                                <h3 className="mt-3 font-semibold">{t(labelKey as never)}</h3>
                                <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                                    {t(bodyKey as never)}
                                </p>
                                <span className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-primary">
                                    {t("landing.persona.open")}
                                    <ArrowRight className="size-3.5 transition-transform group-hover:translate-x-0.5" />
                                </span>
                            </button>
                        ))}
                    </div>
                </section>
            </ScrollReveal>

            {/* Honest footnote */}
            <p className="mt-14 text-center text-xs text-muted-foreground">{t("landing.footnote")}</p>
        </div>
    )
}
